import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { useEffect } from 'react';
import isElectron from 'is-electron';

import { updateUserStatus } from 'flow/actions/headset-actions';
import { getBrand } from 'flow/brands/brands';
import { selectMissedCallCount } from 'flow/selectors/app-selectors';
import { useSyncCallSettings } from 'flow/hooks/api/useSoftphoneCallSettings';
import {
    selectNumberOfCentionErrandsRelevantForUser,
    selectUserHasCentionFeature,
} from 'flow/selectors/CentionOmniSelectors';
import { selectNumberUnreadChannelMessages } from 'flow/selectors/channel-selectors';
import { selectNumberUnreadChatMessages } from 'flow/selectors/chat-selectors';
import {
    selectNumberUnreadVoiceMessages,
    selectOwnExtensionStatus,
} from 'flow/selectors/ExtensionSelectors';
import { selectNumberOfItemsForBadgeInAgentView } from 'flow/selectors/TicketsSelectors';
import { userLogEntry } from 'flow/utils/browser-utils';
import { electronActions } from 'flow/utils/electron-utils';

const DocumentTitleUserStatusHandler = (): null => {
    const dispatch = useAppDispatch();
    useSyncCallSettings();
    const isLoggedIn = useAppSelector(
        (state) => state.app.get('state') === 'ready',
    );
    const loggedInUserStatus = useAppSelector(selectOwnExtensionStatus);

    const userHasCentionOmniFeature = useAppSelector(
        selectUserHasCentionFeature,
    );
    const centionErrandsCount = useAppSelector((state) =>
        userHasCentionOmniFeature
            ? selectNumberOfCentionErrandsRelevantForUser(state)
            : 0,
    );

    const ticketsCount =
        useAppSelector(selectNumberOfItemsForBadgeInAgentView) || 0;

    const missedCallCount = useAppSelector(selectMissedCallCount);

    const totalNumberUnreadMessages = useAppSelector(
        selectNumberUnreadChatMessages,
    );

    const totalNumberUnreadVoiceMessages = useAppSelector(
        selectNumberUnreadVoiceMessages,
    );

    const totalNumberUnreadChannelMessages = useAppSelector(
        selectNumberUnreadChannelMessages,
    );

    useEffect(() => {
        const unread =
            totalNumberUnreadMessages +
            totalNumberUnreadVoiceMessages +
            totalNumberUnreadChannelMessages +
            missedCallCount +
            centionErrandsCount +
            ticketsCount;

        electronActions.sendTotalUnreadMessages(dispatch, unread);

        const brandName = getBrand(true).brandNameUIFriendly;
        document.title =
            isElectron() || !unread ? brandName : `(${unread}) ${brandName}`;
    }, [
        missedCallCount,
        centionErrandsCount,
        ticketsCount,
        dispatch,
        totalNumberUnreadMessages,
        totalNumberUnreadVoiceMessages,
        totalNumberUnreadChannelMessages,
    ]);

    useEffect(() => {
        if (loggedInUserStatus) {
            userLogEntry(`User status changed to ${loggedInUserStatus}`);
            dispatch(updateUserStatus(loggedInUserStatus));

            if (!isLoggedIn) {
                electronActions.sendUserLoggedOut();
            }
        }
    }, [dispatch, loggedInUserStatus, isLoggedIn]);

    return null;
};

export default DocumentTitleUserStatusHandler;
